// src/pages/SignIn.tsx
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Heart, Lock, Rocket, X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { cn } from "../utils/helpers";
import logo from "../assets/img/logo_gold.png";
import { useAppStore } from "../store/app.store";

export default function SignIn() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signInWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pendingAuthRedirect = useAppStore((state) => state.pendingAuthRedirect);
  const clearPendingAuthRedirect = useAppStore((state) => state.clearPendingAuthRedirect);

  useEffect(() => {
    if (!isAuthenticated) return;

    const locationStateReturnTo = (location.state as any)?.returnTo;
    const returnTo = locationStateReturnTo || pendingAuthRedirect?.returnTo || "/dashboard";

    clearPendingAuthRedirect();
    navigate(returnTo, { replace: true });
  }, [isAuthenticated, navigate, pendingAuthRedirect, clearPendingAuthRedirect, location.state]);

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    try {
      const success = await signInWithGoogle();
      if (!success) {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Sign in error:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background text-text-primary flex items-center justify-center p-4"
    >
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent" />
      </div>

      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="fixed text-primary/20"
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            scale: 0,
          }}
          animate={{
            y: [null, -100, -200],
            scale: [0, 1, 0],
            opacity: [0, 0.5, 0],
          }}
          transition={{
            duration: 10 + Math.random() * 10,
            repeat: Infinity,
            delay: Math.random() * 5,
          }}
        >
          <Heart size={24 + Math.random() * 40} />
        </motion.div>
      ))}

      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="relative w-full max-w-md"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-intense rounded-[32px] blur-xl opacity-20" />

        <div className="relative bg-surface/80 backdrop-blur-2xl border border-white/10 rounded-[32px] p-4 shadow-2xl">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.3,
              }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl mb-4"
            >
              <img src={logo} alt="LRNSY logo" />
            </motion.div>

            <h1 className="text-3xl md:text-4xl text-center font-black mb-2">
              Welcome to{" "}
              <span
                className="font-black"
                style={{
                  background: "linear-gradient(90deg, #ffffffff, #3c3c3eff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                }}
              >
                Lrnsy
              </span>
            </h1>

            <p className="text-text-tertiary text-sm">Sign in to finalize and save your surprise</p>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            className={cn(
              "relative w-full py-4 px-6 rounded-xl font-bold text-white transition-all overflow-hidden group",
              "bg-gradient-to-r from-primary to-primary-intense hover:shadow-xl hover:shadow-primary/30",
              isSubmitting && "opacity-70 cursor-not-allowed"
            )}
          >
            <span className="relative z-10 flex items-center justify-center gap-3">
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                  Signing in...
                </>
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </>
              )}
            </span>
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transition-transform duration-700" />
          </button>

          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2">
                <Rocket size={20} className="text-primary mx-auto mb-1" />
                <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider">
                  One Click
                </p>
              </div>
              <div className="p-2">
                <X size={20} className="text-primary mx-auto mb-1" />
                <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider">
                  No Password
                </p>
              </div>
              <div className="p-2">
                <Lock size={20} className="text-primary mx-auto mb-1" />
                <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider">
                  Secure
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
